import IconButton from 'components/IconButton';
import moment from 'moment';
import React, { useState } from 'react';
import { Collapse } from 'reactstrap';

import { ChartType } from '../types';
import { getMetricOutcome, getSectionStatus } from './helper';
import KPIBarOrDoughnutChart from './KPIBarOrDoughnutChart';
import KPIChartGroup from './KPIChartGroup';
import KPIHistoryChart from './KPIHistoryChart';
import styles from './styles.module.css';
import { KPIHistoryPoint, KPIMetric, KPISection, KPIUnit } from './types';

interface KPISectionChartsProps {
  section: KPISection;
}

// Reuse the Policy Compliance palette so KPI charts look native to the
// existing dashboard: teal for positive, muted red for risk, and blue series.
const POLICY_TEAL = '#5FB3B3';
const POLICY_RED = '#B85050';
const POLICY_BLUE = '#0096FF';
const POLICY_NEUTRAL = '#D3D3D3';

const outcomeColors = {
  GOOD: POLICY_TEAL,
  BAD: POLICY_RED,
  NEUTRAL: POLICY_NEUTRAL,
};

// These values mirror the status indicators in styles.module.css so each
// history line and the traffic light in its header communicate one status.
const historyOutcomeColors = {
  GOOD: '#3c8b65',
  BAD: '#c65353',
  NEUTRAL: '#8a94a6',
};

const unitLabels: Record<KPIUnit, string> = {
  count: 'Count',
  days: 'Days',
  percent: 'Percent',
};

const valueTypeLabels: Record<KPIUnit, string> = {
  count: 'Count',
  days: 'Days',
  percent: 'Percent',
};

const numericValueFormatters: Record<KPIUnit, (value: number) => string> = {
  // Chart headers already identify the unit, so plotted values stay numeric.
  count: (value) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
      value,
    ),
  days: (value) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
      value,
    ),
  percent: (value) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
      value,
    ),
};

const getMetricsByUnit = (metrics: KPIMetric[]) =>
  metrics.reduce<Partial<Record<KPIUnit, KPIMetric[]>>>((groups, metric) => {
    groups[metric.unit] = [...(groups[metric.unit] || []), metric];
    return groups;
  }, {});

type KPIComparableMetric = KPIMetric & {
  previous_value: number;
  value: number;
};

type KPIMetricWithHistory = KPIMetric & {
  history_points: KPIHistoryPoint[];
};

const hasComparableValues = (
  metric: KPIMetric,
): metric is KPIComparableMetric =>
  metric.value !== null && metric.previous_value !== null;

const hasHistory = (metric: KPIMetric): metric is KPIMetricWithHistory =>
  Boolean(metric.history_points?.length);

const getStatusIndicator = (metrics: KPIMetric[], kpiName?: string) => {
  const status = getSectionStatus(metrics);
  const statusLabel = kpiName
    ? `${kpiName} status: ${status}`
    : `Overall KPI status: ${status}`;
  return (
    <span
      aria-label={statusLabel}
      className={`${styles.chartOverallStatus} ${
        styles[`chartOverallStatus${status.replace(' ', '')}`]
      }`}
      title={statusLabel}
    />
  );
};

const getChartHeaderActions = (
  metrics: KPIMetric[],
  valueTypeLabel: string,
  kpiName?: string,
) => (
  <div className={styles.chartHeaderActions}>
    <span className={styles.chartValueType}>{valueTypeLabel}</span>
    {getStatusIndicator(metrics, kpiName)}
  </div>
);

const KPISectionCharts = ({ section }: KPISectionChartsProps) => {
  const [overviewOpen, setOverviewOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [showHistoryValues, setShowHistoryValues] = useState(false);
  const [expandedOverviewChartKey, setExpandedOverviewChartKey] = useState<
    string | null
  >(null);
  const [expandedHistoryChartKey, setExpandedHistoryChartKey] = useState<
    string | null
  >(null);
  const metricsByUnit = getMetricsByUnit(section.metrics);
  const outcomes = section.metrics.map(getMetricOutcome);
  // Reuse the card's status calculation so detail charts communicate the
  // same overall traffic-light result as the KPI dashboard tile.
  const sectionStatus = getSectionStatus(section.metrics);
  const statusCounts = [
    outcomes.filter((outcome) => outcome === 'GOOD').length,
    outcomes.filter((outcome) => outcome === 'BAD').length,
    outcomes.filter((outcome) => outcome === 'NEUTRAL').length,
  ];
  const comparisonGroups = Object.entries(metricsByUnit)
    .map(([unit, metrics]) => [unit, metrics.filter(hasComparableValues)])
    .filter(([, metrics]) => metrics.length > 0) as [
    KPIUnit,
    KPIComparableMetric[],
  ][];
  const historyMetrics = section.metrics.filter(hasHistory);

  const toggleOverviewChartExpansion = (chartKey: string) => {
    setExpandedOverviewChartKey((currentKey) =>
      currentKey === chartKey ? null : chartKey,
    );
  };

  const toggleHistoryChartExpansion = (chartKey: string) => {
    setExpandedHistoryChartKey((currentKey) =>
      currentKey === chartKey ? null : chartKey,
    );
  };

  const renderStatusChart = (isActive: boolean, key?: string) => (
    <KPIBarOrDoughnutChart
      chartType={ChartType.Doughnut}
      colors={[POLICY_TEAL, POLICY_RED, POLICY_NEUTRAL]}
      containerClassName={styles.policyChartPanel}
      datasets={[statusCounts]}
      isActive={isActive}
      key={key}
      labels={['Increasing', 'Decreasing', 'Unchanged']}
      title="Status"
      tooltipLabels={['KPIs']}
      valueFormatter={numericValueFormatters.count}
      onExpandClick={() => toggleOverviewChartExpansion('status')}
    />
  );

  // Previous values stay blue while each current bar mirrors its KPI's
  // Increasing, Decreasing, or Unchanged traffic-light outcome.
  const renderComparisonChart = (
    unit: KPIUnit,
    metrics: KPIComparableMetric[],
    isActive: boolean,
    key?: string,
  ) => (
    <KPIBarOrDoughnutChart
      barDatasetColors={[
        POLICY_BLUE,
        metrics.map((metric) => outcomeColors[getMetricOutcome(metric)]),
      ]}
      chartType={ChartType.Bar}
      colors={[POLICY_BLUE, POLICY_TEAL]}
      containerClassName={styles.policyChartPanel}
      datasets={[
        metrics.map((metric) => metric.previous_value),
        metrics.map((metric) => metric.value),
      ]}
      isActive={isActive}
      key={key}
      labels={metrics.map((metric) => metric.label)}
      title={`${unitLabels[unit]}: Current vs Previous`}
      tooltipLabels={['Previous', 'Current']}
      valueFormatter={numericValueFormatters[unit]}
      onExpandClick={() => toggleOverviewChartExpansion(unit)}
    />
  );

  const renderHistoryChart = (
    metric: KPIMetricWithHistory,
    isActive: boolean,
    key?: string,
  ) => (
    <KPIHistoryChart
      // Match each history series to the goal-aware status indicator shown in
      // its chart header: positive, negative, or unchanged/unavailable.
      colors={[historyOutcomeColors[getMetricOutcome(metric)]]}
      containerClassName={styles.policyChartPanel}
      datasets={[metric.history_points.map((point) => point.value)]}
      headerActions={getChartHeaderActions(
        [metric],
        valueTypeLabels[metric.unit],
        metric.label,
      )}
      isActive={isActive}
      key={key}
      labels={metric.history_points.map((point) =>
        moment(point.period_end).format('MMM D'),
      )}
      newDataGenerationIndex={0}
      showCounts={showHistoryValues}
      summaryValue={
        metric.value === null
          ? 'N/A'
          : numericValueFormatters[metric.unit](metric.value)
      }
      title={metric.label}
      tooltipLabels={[{ label: metric.label, hostsCount: null }]}
      valueFormatter={numericValueFormatters[metric.unit]}
      onExpandClick={() => toggleHistoryChartExpansion(metric.key)}
    />
  );

  return (
    <>
      <KPIChartGroup
        groupId="kpi-current-overview"
        isOpen={overviewOpen}
        title="Current KPI Overview"
        onToggle={() => setOverviewOpen((open) => !open)}
      >
        <Collapse
          isOpen={Boolean(expandedOverviewChartKey)}
          className={styles.expandedChartRow}
        >
          {expandedOverviewChartKey === 'status' &&
            renderStatusChart(true, 'expanded-status')}
          {comparisonGroups.map(([unit, metrics]) =>
            expandedOverviewChartKey === unit
              ? renderComparisonChart(unit, metrics, true, `expanded-${unit}`)
              : null,
          )}
        </Collapse>
        <div className={styles.detailChartGrid}>
          {renderStatusChart(expandedOverviewChartKey === 'status', 'status')}

          {comparisonGroups.map(([unit, metrics]) =>
            renderComparisonChart(
              unit,
              metrics,
              expandedOverviewChartKey === unit,
              unit,
            ),
          )}
        </div>
      </KPIChartGroup>

      {historyMetrics.length > 0 && (
        <KPIChartGroup
          actions={
            <IconButton
              id="kpi-history-values-toggle"
              iconClass={
                showHistoryValues ? 'fa fa-eye-slash fa-lg' : 'fa fa-eye fa-lg'
              }
              size="sm"
              tooltip={
                showHistoryValues ? 'Hide chart values' : 'Show chart values'
              }
              active={showHistoryValues}
              onClick={() => setShowHistoryValues((show) => !show)}
            />
          }
          groupId="kpi-trending-history"
          isOpen={historyOpen}
          status={sectionStatus}
          title="KPI Trending History"
          onToggle={() => setHistoryOpen((open) => !open)}
        >
          <Collapse
            isOpen={Boolean(expandedHistoryChartKey)}
            className={styles.expandedChartRow}
          >
            {historyMetrics.map((metric) =>
              expandedHistoryChartKey === metric.key
                ? renderHistoryChart(metric, true, `expanded-${metric.key}`)
                : null,
            )}
          </Collapse>
          <div className={styles.detailTrendGrid}>
            {historyMetrics.map((metric) =>
              renderHistoryChart(
                metric,
                expandedHistoryChartKey === metric.key,
                metric.key,
              ),
            )}
          </div>
        </KPIChartGroup>
      )}
    </>
  );
};

export default KPISectionCharts;
